import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { AiMetricsResponse, MetricDatapoint } from '../../services/api';
import { RefreshCw, Loader2, Cpu, DollarSign, AlertTriangle } from 'lucide-react';

export function AiUsage() {
  const [data, setData] = useState<AiMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getAiMetrics();
      setData(result);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load AI metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">AI Usage</h1>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : data ? (
        <>
          {/* Bedrock Cost */}
          {data.bedrock_cost_30d !== null && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6 text-green-500" />
                <span className="text-sm text-gray-500">Bedrock Cost (Last 30 Days)</span>
              </div>
              <div className="text-3xl font-bold text-gray-800">
                ${Number(data.bedrock_cost_30d).toFixed(2)}
              </div>
            </div>
          )}

          {/* Lambda Invocations */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5" /> Generation Lambda Invocations (30 Days)
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 text-gray-600 font-medium">Lambda Function</th>
                    <th className="text-right py-2 px-4 text-gray-600 font-medium">Total Invocations</th>
                    <th className="text-right py-2 px-4 text-gray-600 font-medium">Total Errors</th>
                    <th className="text-right py-2 px-4 text-gray-600 font-medium">Error Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(data.invocations || {}).map(name => {
                    const invocs = (data.invocations[name] || []).reduce((sum: number, dp: MetricDatapoint) => sum + dp.count, 0);
                    const errs = (data.errors[name] || []).reduce((sum: number, dp: MetricDatapoint) => sum + dp.count, 0);
                    const errorRate = invocs > 0 ? ((errs / invocs) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={name} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-4 font-mono text-sm">{name}</td>
                        <td className="py-2 px-4 text-right">{invocs.toLocaleString()}</td>
                        <td className="py-2 px-4 text-right">{errs.toLocaleString()}</td>
                        <td className="py-2 px-4 text-right">
                          <span className={errs > 0 ? 'text-red-600' : 'text-green-600'}>
                            {errorRate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Weekly Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Weekly Invocation Breakdown</h2>
            {Object.entries(data.invocations || {}).map(([name, datapoints]: [string, MetricDatapoint[]]) => (
              datapoints.length > 0 && (
                <div key={name} className="mb-4">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">{name}</h3>
                  <div className="flex gap-2">
                    {datapoints.map((dp: MetricDatapoint, i: number) => (
                      <div key={i} className="flex-1 text-center p-2 bg-gray-50 rounded">
                        <div className="text-xs text-gray-500">
                          {new Date(dp.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="font-semibold">{dp.count}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
